import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { BookingMode } from "@/data/certificateBookingActions";
import { useLang } from "@/i18n/LangContext";

const HLTHY_ORIGIN = "https://intygslakaren.hlthy.se";

type RedirectOptions = {
  active?: boolean;
  slug?: string | null;
  mode?: BookingMode | null;
};

const isBookingConfirmationMessage = (data: unknown) => {
  const dataString = typeof data === "string" ? data : JSON.stringify(data ?? {});
  if (typeof data === "object" && data !== null) {
    const payload = data as Record<string, unknown>;
    if (
      payload.type === "booking-complete" ||
      payload.type === "booking-confirmed" ||
      payload.type === "redirect" ||
      payload.redirect === "/bekraftelse" ||
      payload.redirectTo === "/bekraftelse" ||
      payload.url === "/bekraftelse"
    ) {
      return true;
    }
  }

  return (
    dataString.includes("bekraftelse") ||
    dataString.includes("confirmation") ||
    dataString.includes("booking-complete")
  );
};

const buildConfirmationPath = (
  lang: "sv" | "en",
  slug?: string | null,
  mode?: BookingMode | null,
) => {
  const base = lang === "sv" ? "/bekraftelse" : "/en/confirmation";
  const params = new URLSearchParams();
  if (slug) params.set("intyg", slug);
  if (mode) params.set("besok", mode);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
};

/**
 * Listens for HLTHY booking-iframe postMessage events and navigates
 * to /bekraftelse (or /en/confirmation) when a booking is confirmed.
 */
export function useBookingConfirmationRedirect(options: RedirectOptions | boolean = true) {
  const navigate = useNavigate();
  const { lang } = useLang();
  const redirected = useRef(false);

  const active = typeof options === "boolean" ? options : (options.active ?? true);
  const slug = typeof options === "boolean" ? null : (options.slug ?? null);
  const mode = typeof options === "boolean" ? null : (options.mode ?? null);

  useEffect(() => {
    if (!active) return;
    redirected.current = false;

    const confirmationPath = buildConfirmationPath(lang, slug, mode);

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== HLTHY_ORIGIN) return;
      if (redirected.current) return;
      if (!isBookingConfirmationMessage(event.data)) return;

      redirected.current = true;
      navigate(confirmationPath);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [active, lang, mode, navigate, slug]);
}
